// src/utils/imageCompress.ts
export type CompressOptions = {
  maxDim?: number; // max width/height in px
  quality?: number; // 0..1
  mimeType?: 'image/jpeg' | 'image/webp';
};

type DrawableImage = ImageBitmap | HTMLImageElement;

const loadImageElement = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image file'));
    };

    img.decoding = 'async';
    img.src = url;
  });

const loadDrawable = async (file: File): Promise<DrawableImage> => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' } as any);
    } catch {
      // iOS/HEIC/Safari can fail here; fall back to regular image decoding.
    }
  }

  return await loadImageElement(file);
};

const getDrawableSize = (drawable: DrawableImage) => {
  const width = Number((drawable as any).width || 0);
  const height = Number((drawable as any).height || 0);
  return {
    width: Number.isFinite(width) && width > 0 ? width : 1,
    height: Number.isFinite(height) && height > 0 ? height : 1,
  };
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mimeType: 'image/jpeg' | 'image/webp',
  quality: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob() returned null'))),
      mimeType,
      quality,
    );
  });

export async function compressImageFile(
  file: File,
  opts: CompressOptions = {},
): Promise<Blob> {
  const maxDim = opts.maxDim ?? 1280;
  const quality = Math.min(0.92, Math.max(0.45, opts.quality ?? 0.82));
  const mimeType = opts.mimeType ?? 'image/jpeg';

  const drawable = await loadDrawable(file);

  try {
    const { width, height } = getDrawableSize(drawable);
    const longestEdge = Math.max(width, height, 1);
    const scale = Math.min(1, maxDim / longestEdge);
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D context unavailable');

    // JPEG does not preserve transparency. White fill prevents transparent PNGs
    // from turning black after camera-safe conversion.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(drawable, 0, 0, targetW, targetH);

    return await canvasToBlob(canvas, mimeType, quality);
  } finally {
    if ('close' in drawable && typeof drawable.close === 'function') {
      drawable.close();
    }
  }
}
